import {
  afterRenderEffect,
  Component,
  computed,
  ElementRef,
  input,
  linkedSignal,
  output,
  viewChild,
} from '@angular/core';
import { SiloTicketItem } from '../silo-ticket-item/silo-ticket-item';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { TicketState } from '../../Services/ticket-state';
import type { TicketColumnState } from '../../Services/ticket-state';
import type { TicketCardItem } from '../../Services/EntiteProjet';
export { TicketState };
export type { TicketColumnState } from '../../Services/ticket-state';
export type { TicketCardItem } from '../../Services/EntiteProjet';

@Component({
  selector: 'app-silo-tickets',
  imports: [SiloTicketItem, MatProgressSpinnerModule],
  templateUrl: './silo-tickets.html',
  styleUrl: './silo-tickets.css',
  host: { '[class.ticket-silo-replie]': 'replie()' },
})
export class SiloTickets {
  readonly title = input('');
  readonly count = input(0);
  readonly badgeCount = input(0);
  readonly countLabel = computed(() => this.formaterCompteur(this.count()));
  readonly badgeCountLabel = computed(() => this.formaterCompteur(this.badgeCount()));
  readonly state = input<TicketColumnState>(TicketState.nouveau);
  readonly tickets = input<TicketCardItem[]>([]);
  readonly showSiteName = input(true);
  readonly hasMore = input(false);
  readonly erreur = input('');
  readonly reessayer = output<void>();
  readonly loadingMore = input(false);
  readonly dragActive = input(false);
  readonly canDrop = input(false);
  readonly isDropTarget = input(false);
  readonly isDisabledTarget = input(false);
  readonly isInvalidTarget = input(false);
  readonly itemsDraggable = input(false);
  readonly invalidMessage = input('');

  readonly loadMore = output<void>();
  readonly ticketDragStarted = output<TicketCardItem>();
  readonly ticketDragEnded = output<void>();
  readonly ticketClicked = output<TicketCardItem>();
  readonly siloDragEntered = output<void>();
  readonly siloDragLeft = output<void>();
  readonly siloDropped = output<void>();
  readonly siloInvalidDropped = output<void>();

  /** Résolu démarre replié ; un rechargement des tickets conserve le choix de l'utilisateur. */
  readonly replie = linkedSignal(() => this.state() === TicketState.resolu);
  private static prochainId = 0;
  /** Lie le bouton à sa liste sans collision entre les silos présents sur la page. */
  readonly listeId = `ticket-silo-list-${SiloTickets.prochainId++}`;

  private readonly finListe = viewChild<ElementRef<HTMLElement>>('finListe');

  constructor() {
    afterRenderEffect((onCleanup) => {
      const finListe = this.finListe()?.nativeElement;
      // Réévaluer la visibilité après l’ajout d’une page, même si la liste reste courte.
      this.tickets();
      if (!finListe || typeof IntersectionObserver === 'undefined') return;

      let chargementDemande = false;
      const observer = new IntersectionObserver((entries) => {
        const visible = entries.some((entry) => entry.isIntersecting);
        if (!visible) {
          chargementDemande = false;
          return;
        }
        if (
          this.erreur() ||
          chargementDemande ||
          this.loadingMore() ||
          !this.hasMore() ||
          this.replie()
        )
          return;
        chargementDemande = true;
        this.loadMore.emit();
      });

      // Le viewport tient aussi compte du défilement interne des silos sur ordinateur.
      observer.observe(finListe);
      onCleanup(() => observer.disconnect());
    });
  }

  trackByReference(_: number, ticket: TicketCardItem) {
    return ticket.id ?? ticket.reference;
  }

  /** Arrondi à la centaine jusqu’à 10k, puis milliers entiers tronqués. */
  private formaterCompteur(nombre: number): string {
    if (nombre < 1000) return String(nombre);
    if (nombre >= 10000) return `${Math.floor(nombre / 1000)}k`;
    const centaines = Math.round(nombre / 100);
    const milliers = Math.floor(centaines / 10);
    const decimale = centaines % 10;
    return `${milliers}${decimale ? '.' + decimale : ''}k`;
  }

  basculerRepli() {
    this.replie.update((valeur) => !valeur);
  }

  onDragOver(event: DragEvent) {
    if (!this.dragActive()) {
      return;
    }

    event.preventDefault();
    if (event.dataTransfer) {
      event.dataTransfer.dropEffect = this.canDrop() ? 'move' : 'none';
    }
  }

  /** Le dépôt reste actif sur le silo replié ; il ne modifie pas son état d'ouverture. */
  onDrop(event: DragEvent) {
    if (!this.dragActive()) {
      return;
    }

    event.preventDefault();
    if (this.canDrop()) {
      this.siloDropped.emit();
      return;
    }

    this.siloInvalidDropped.emit();
  }
}
